import type { Project } from "@proof/core";

/** These have no equivalent column in the v6 row exporter. Preserving JSON is not execution support. */
export function LegacyFeatureSummary({ project }: { project: Project }) {
  const fields = project.fields;
  const items = [
    {
      name: "Calculated rules",
      used: fields.some((field) => !!field.calculation),
    },
    {
      name: "Native formatting (number/date/case/prefix/suffix)",
      used: fields.some(
        (field) =>
          (field.format.kind && field.format.kind !== "raw") ||
          field.format.uppercase ||
          field.format.prefix ||
          field.format.suffix,
      ),
    },
    {
      name: "Extended validation (patterns and numeric limits)",
      used: fields.some(
        (field) =>
          field.validation.pattern ||
          field.validation.min !== undefined ||
          field.validation.max !== undefined,
      ),
    },
    {
      name: "Dynamic data source parameter forms",
      used: !!project.dynamicSources?.length,
    },
    {
      name: "QR defaults and address/coordinate modes",
      used: fields.some(
        (field) =>
          !!field.qr &&
          (!!Object.keys(field.qr.defaults || {}).length ||
            !!field.qr.locationMode),
      ),
    },
    {
      name: "Map editor and mapped address defaults",
      used: fields.some((field) => !!field.map),
    },
    {
      name: "Grouped document layout and text alignment",
      used: project.pages.some((page) => !!page.layoutGroups?.length),
    },
    {
      name: "Default dataset ordering",
      used: project.datasets.some((dataset) => !!dataset.sort),
    },
  ];
  return (
    <details className="panel padded">
      <summary>Proofing editor v6 compatibility</summary>
      <p>
        These features require the Proof Studio adapter and extended data. The
        v6 table payload alone does not implement them. Keep{" "}
        <code>extra_json_data</code> in your central store and use the hosted
        proof generator for their behavior. Disabling an editor control
        preserves existing project data.
      </p>
      <ul>
        {items.map((item) => (
          <li key={item.name}>
            {item.name}
            {item.used
              ? " — used in this form"
              : " — available in Proof Studio"}
          </li>
        ))}
      </ul>
      <p>
        Basic inputs, fixed choices, dataset links, and legacy statement IDs
        have v6 table mappings. Native calculated rules are not translated into
        executable legacy JavaScript.
      </p>
    </details>
  );
}
